import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import Planet from "@/components/Planet";
import { Button } from "@/components/ui/button";
import { setRubric, setComment } from "@/lib/api";
import { printCertificate } from "@/lib/certificate";
import Rocket from "@/components/Rocket";
import { renderToStaticMarkup } from "react-dom/server";
import { CalendarDays, Printer, Award } from "lucide-react";

export const RUBRIC_LEVELS = [
  { v: 0, l: "—" },
  { v: 1, l: "Beginning" },
  { v: 2, l: "Approaching" },
  { v: 3, l: "Meeting" },
  { v: 4, l: "Exceeding" },
];

// Merge live (active project) + stored progress into a uniform shape.
export function getProjectData(project, room) {
  const active = project.id === room.activeProjectId;
  const stages = active ? room.stages : project.stages;
  const pupils = room.pupils
    .filter((p) => !p.isTeacher)
    .map((p) => {
      const prog = active
        ? { stageIndex: p.stageIndex, history: p.history || [] }
        : (p.projects || {})[project.id] || { stageIndex: 0, history: [] };
      return { id: p.id, name: p.name, color: p.color, variant: p.variant, ...prog };
    });
  return { stages, pupils };
}

function fmtDate(d) {
  if (!d) return null;
  return new Date(d + "T00:00:00").toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function TimelineDialog({ open, onOpenChange, room }) {
  const projects = room?.projects || [];
  const [projId, setProjId] = useState(room?.activeProjectId || projects[0]?.id);
  if (!room) return null;
  const project = projects.find((p) => p.id === projId) || projects[0];
  const { stages, pupils } = getProjectData(project, room);

  const conquered = (i) => pupils.length > 0 && pupils.every((pp) => pp.stageIndex >= i);
  const conqueredDate = (i) => {
    const dates = pupils
      .map((pp) => (pp.history || []).find((h) => h.stageIndex === i)?.date)
      .filter(Boolean);
    if (dates.length === 0) return null;
    return dates.sort().slice(-1)[0]; // latest = when the last pupil arrived
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="timeline-dialog" className="bg-[#141738] border-[#252B5C] text-white max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-cyan-300 flex items-center gap-2">
            <CalendarDays className="w-6 h-6" /> Class Timeline
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            The date the whole class conquered each planet.
          </DialogDescription>
        </DialogHeader>

        <select
          data-testid="timeline-project-select"
          value={project.id}
          onChange={(e) => setProjId(e.target.value)}
          className="bg-[#0E1126] border border-[#252B5C] rounded-md h-10 px-2 text-white w-full mb-3"
        >
          {projects.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>

        <div className="relative pl-4">
          <div className="absolute left-[26px] top-2 bottom-2 w-0.5 bg-[#252B5C]" />
          {stages.map((s, i) => {
            if (i === 0) return null;
            const done = conquered(i);
            const date = conqueredDate(i);
            return (
              <div key={i} data-testid={`timeline-stage-${i}`} className="flex items-center gap-3 mb-3 relative">
                <div className={`shrink-0 ${done ? "" : "opacity-40"}`}>
                  <Planet index={i} total={stages.length} color={project.color} planet={s.planet} size={42} />
                </div>
                <div className="flex-1">
                  <p className="font-heading font-semibold">{s.name}</p>
                  {done ? (
                    <p className="text-sm text-emerald-300">
                      🚩 Whole class{date ? ` on ${fmtDate(date)}` : " (date not recorded)"}
                    </p>
                  ) : (
                    <p className="text-sm text-slate-500">
                      {pupils.filter((pp) => pp.stageIndex >= i).length}/{pupils.length} pupils here
                    </p>
                  )}
                </div>
              </div>
            );
          })}
          {pupils.length === 0 && <p className="text-slate-400 text-sm">No pupils in this project yet.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function levelLabel(criterion, value) {
  if (!value) return "—";
  const lv = (criterion.levels || []).find((l) => l.points === value);
  return lv ? `${lv.points}. ${lv.label}` : String(value);
}

// Builds a printable end-of-term HTML doc: one condensed class page + one detailed page per pupil.
function buildReportHtml(room, project, stages) {
  const { pupils } = getProjectData(project, room);
  const last = stages.length - 1;
  const criteria = project.rubric || [];
  const esc = (s) => String(s == null ? "" : s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const date = new Date().toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
  const progOf = (id) => (room.pupils.find((x) => x.id === id)?.projects || {})[project.id] || {};

  const summaryRows = pupils.map((pu) => {
    const prog = progOf(pu.id);
    const asr = prog.assessment;
    const marks = prog.rubric || {};
    const critCells = criteria.map((c) => `<td>${esc(levelLabel(c, marks[c.id] || 0))}</td>`).join("");
    return `<tr><td class="name">${esc(pu.name)}</td><td>${pu.stageIndex >= last ? "Complete" : pu.stageIndex + "/" + last}</td><td>${asr ? asr.percent + "% " + (asr.passed ? "Pass" : "Fail") : "—"}</td>${critCells}</tr>`;
  }).join("");

  const summary = `
    <section class="page">
      <p class="brand">🚀 Stellar Steps</p>
      <h1>${esc(room.name)} — Class Report</h1>
      <p class="sub">Project: <b>${esc(project.name)}</b>${room.teacherName ? " · Teacher: " + esc(room.teacherName) : ""} · ${date}</p>
      <table>
        <thead><tr><th>Pupil</th><th>Journey</th><th>Test</th>${criteria.map((c) => `<th>${esc(c.text)}</th>`).join("")}</tr></thead>
        <tbody>${summaryRows || `<tr><td colspan="${3 + criteria.length}">No pupils yet.</td></tr>`}</tbody>
      </table>
    </section>`;

  const pupilPages = pupils.map((pu) => {
    const prog = progOf(pu.id);
    const asr = prog.assessment;
    const marks = prog.rubric || {};
    const rubricRows = criteria.map((c) => {
      const val = marks[c.id] || 0;
      const lv = (c.levels || []).find((l) => l.points === val);
      return `<tr><td class="name">${esc(c.text)}</td><td>${esc(levelLabel(c, val))}</td><td>${esc(lv ? lv.descriptor : "")}</td></tr>`;
    }).join("");
    return `
      <section class="page">
        <h1>${esc(pu.name)}</h1>
        <p class="sub">${esc(room.name)} · ${esc(project.name)} · ${date}</p>
        <div class="stat"><span>Journey</span><b>${pu.stageIndex >= last ? "Complete 🏆" : `Stage ${pu.stageIndex} of ${last}`}</b></div>
        <div class="stat"><span>Exploration Test</span><b>${asr ? `${asr.percent}% — ${asr.passed ? "Passed" : "Not yet"}${asr.attempts ? ` (${asr.attempts} attempt${asr.attempts > 1 ? "s" : ""})` : ""}` : "Not taken"}</b></div>
        ${criteria.length ? `<h2>Rubric</h2><table><thead><tr><th>Standard</th><th>Level</th><th>What this means</th></tr></thead><tbody>${rubricRows}</tbody></table>` : ""}
        <h2>Teacher comment</h2>
        <div class="comment">${esc(prog.comment) || "<i>No comment.</i>"}</div>
      </section>`;
  }).join("");

  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(room.name)} report</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: Georgia, 'Times New Roman', serif; color: #14183a; margin: 0; }
    .page { padding: 32px 40px; page-break-after: always; }
    .page:last-child { page-break-after: auto; }
    .brand { font-family: Arial, sans-serif; font-weight: bold; letter-spacing: .5px; color: #6d28d9; margin: 0 0 6px; font-size: 13px; text-transform: uppercase; }
    h1 { font-size: 26px; margin: 0 0 4px; color: #1b2a6b; }
    h2 { font-size: 16px; margin: 20px 0 6px; color: #6d28d9; border-bottom: 2px solid #eee; padding-bottom: 3px; }
    .sub { color: #555; margin: 0 0 18px; font-size: 13px; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    th, td { border: 1px solid #d0d3e6; padding: 7px 9px; text-align: left; vertical-align: top; }
    th { background: #f1f2fb; color: #1b2a6b; }
    td.name { font-weight: bold; }
    .stat { display: flex; justify-content: space-between; border-bottom: 1px dashed #ccc; padding: 8px 2px; font-size: 14px; }
    .stat span { color: #555; }
    .comment { border: 1px solid #d0d3e6; border-radius: 8px; padding: 12px; min-height: 60px; font-size: 14px; background: #fafbff; white-space: pre-wrap; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style></head><body>${summary}${pupilPages}</body></html>`;
}

export function ClassReportDialog({ open, onOpenChange, room, onRoom }) {
  const projects = room?.projects || [];
  const [projId, setProjId] = useState(room?.activeProjectId || projects[0]?.id);
  if (!room) return null;
  const project = projects.find((p) => p.id === projId) || projects[0];
  const { stages, pupils } = getProjectData(project, room);
  const last = stages.length - 1;
  const criteria = project.rubric || [];

  const markRubric = async (pupilId, critId, level) => {
    const pupil = room.pupils.find((p) => p.id === pupilId);
    const cur = { ...((pupil.projects || {})[project.id]?.rubric || {}) };
    cur[critId] = level;
    const d = await setRubric(room.id, pupilId, project.id, cur);
    // reflect locally
    const updated = { ...room, pupils: room.pupils.map((p) => p.id === pupilId ? { ...p, projects: { ...(p.projects || {}), [project.id]: { ...((p.projects || {})[project.id] || { stageIndex: 0, history: [] }), rubric: cur } } } : p) };
    onRoom && onRoom(updated);
  };

  const saveComment = async (pupilId, comment) => {
    await setComment(room.id, pupilId, project.id, comment);
    const updated = { ...room, pupils: room.pupils.map((p) => p.id === pupilId ? { ...p, projects: { ...(p.projects || {}), [project.id]: { ...((p.projects || {})[project.id] || { stageIndex: 0, history: [] }), comment } } } : p) };
    onRoom && onRoom(updated);
  };

  const printReport = () => {
    const html = buildReportHtml(room, project, stages);
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank");
    if (!w) { URL.revokeObjectURL(url); return; }
    w.onload = () => { w.focus(); w.print(); URL.revokeObjectURL(url); };
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="report-dialog" className="bg-[#141738] border-[#252B5C] text-white max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-cyan-300">Class Report</DialogTitle>
          <DialogDescription className="text-slate-400">End-of-term summary: completion, test scores and rubric marks (rubric is teacher-only).</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 mb-3">
          <select data-testid="report-project-select" value={project.id} onChange={(e) => setProjId(e.target.value)} className="flex-1 bg-[#0E1126] border border-[#252B5C] rounded-md h-10 px-2 text-white">
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <Button data-testid="print-report-btn" onClick={printReport} className="bg-gradient-to-r from-cyan-500 to-purple-500 h-10 font-heading font-bold shrink-0">
            <Printer className="w-4 h-4 mr-1" /> Print / PDF
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left">
                <th className="p-2">Pupil</th>
                <th className="p-2">Completed</th>
                <th className="p-2">Test</th>
                {criteria.map((c) => <th key={c.id} className="p-2 text-amber-300 whitespace-nowrap">{c.text}</th>)}
              </tr>
            </thead>
            <tbody>
              {pupils.map((pu) => {
                const full = room.pupils.find((x) => x.id === pu.id);
                const prog = (full.projects || {})[project.id] || {};
                const asr = prog.assessment;
                const marks = prog.rubric || {};
                return (
                  <React.Fragment key={pu.id}>
                  <tr data-testid={`report-row-${pu.id}`} className="border-t border-[#252B5C]">
                    <td className="p-2 font-heading font-semibold whitespace-nowrap">{pu.name}</td>
                    <td className="p-2">{pu.stageIndex >= last ? "✅" : `${pu.stageIndex}/${last}`}</td>
                    <td className="p-2">{asr ? <span className={asr.passed ? "text-emerald-300" : "text-red-300"}>{asr.percent}% {asr.passed ? "Pass" : "Fail"}</span> : <span className="text-slate-500">—</span>}</td>
                    {criteria.map((c) => (
                      <td key={c.id} className="p-2">
                        <select
                          data-testid={`rubric-mark-${pu.id}-${c.id}`}
                          value={marks[c.id] || 0}
                          onChange={(e) => markRubric(pu.id, c.id, Number(e.target.value))}
                          className="bg-[#0E1126] border border-[#252B5C] rounded h-8 px-1 text-xs text-white"
                        >
                          {[{ points: 0, label: "—" }, ...(c.levels || [])].map((lv) => <option key={lv.points} value={lv.points}>{lv.points ? `${lv.points}. ${lv.label}` : lv.label}</option>)}
                        </select>
                      </td>
                    ))}
                  </tr>
                  <tr className="border-t border-[#252B5C]/40">
                    <td colSpan={3 + criteria.length} className="p-2 pt-0">
                      <div className="flex gap-2 items-start">
                        <textarea
                          data-testid={`report-comment-${pu.id}`}
                          defaultValue={prog.comment || ""}
                          onBlur={(e) => saveComment(pu.id, e.target.value)}
                          placeholder={`Teacher comment for ${pu.name} (appears on their printed report)…`}
                          className="flex-1 bg-[#0E1126] border border-[#252B5C] rounded-lg p-2 text-xs text-white min-h-[46px]"
                        />
                        {asr && asr.passed && asr.percent > 80 && Object.keys(marks).length > 0 && (
                          <Button
                            data-testid={`certificate-btn-${pu.id}`}
                            onClick={() => printCertificate({ pupilName: pu.name, projectName: project.name, className: room.name, color: project.color, percent: asr.percent, rocketSvg: renderToStaticMarkup(<Rocket color={full.color} variant={full.variant} size={130} flame={false} />) })}
                            className="shrink-0 h-[46px] font-heading font-bold text-[#0B0D1B]"
                            style={{ backgroundColor: project.color }}
                          >
                            <Award className="w-4 h-4 mr-1" /> Certificate
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {criteria.length === 0 && <p className="text-slate-500 text-xs mt-2">Add rubric criteria in Mission Control → Rubric to mark pupils here.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function TrackerDialog({ open, onOpenChange, room }) {
  const projects = room?.projects || [];
  if (!room) return null;
  const dataByProject = projects.map((p) => ({ project: p, ...getProjectData(p, room) }));
  const pupils = room.pupils.filter((p) => !p.isTeacher);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tracker-dialog" className="bg-[#141738] border-[#252B5C] text-white max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-cyan-300">Class Project Tracker</DialogTitle>
          <DialogDescription className="text-slate-400">
            Where each pupil is on every project — and what's still needed.
          </DialogDescription>
        </DialogHeader>

        {pupils.length === 0 ? (
          <p className="text-slate-400 text-sm">No pupils yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr>
                  <th className="text-left p-2 sticky left-0 bg-[#141738]">Pupil</th>
                  {dataByProject.map(({ project }) => (
                    <th key={project.id} className="text-left p-2 font-heading text-cyan-300 whitespace-nowrap">{project.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pupils.map((pu) => (
                  <tr key={pu.id} data-testid={`tracker-row-${pu.id}`} className="border-t border-[#252B5C]">
                    <td className="p-2 font-heading font-semibold sticky left-0 bg-[#141738] whitespace-nowrap">{pu.name}</td>
                    {dataByProject.map(({ project, stages, pupils: pps }) => {
                      const pp = pps.find((x) => x.id === pu.id) || { stageIndex: 0 };
                      const last = stages.length - 1;
                      const done = pp.stageIndex >= last;
                      const remaining = stages.slice(pp.stageIndex + 1).map((s) => s.name);
                      return (
                        <td key={project.id} className="p-2 align-top">
                          {done ? (
                            <span className="inline-flex items-center gap-1 text-emerald-300 font-semibold">✅ Done</span>
                          ) : (
                            <div>
                              <span className="text-white">{stages[pp.stageIndex]?.icon} {stages[pp.stageIndex]?.name}</span>
                              {remaining.length > 0 && (
                                <p className="text-[11px] text-amber-300/80 mt-0.5">still needs: {remaining.join(", ")}</p>
                              )}
                            </div>
                          )}
                          {(() => {
                            const asr = (room.pupils.find((x) => x.id === pu.id)?.projects || {})[project.id]?.assessment;
                            if (!asr) return null;
                            return (
                              <p data-testid={`tracker-test-${pu.id}`} className={`text-[11px] mt-1 font-semibold ${asr.passed ? "text-emerald-300" : "text-red-300"}`}>
                                Test: {asr.percent}% {asr.passed ? "✅ Pass" : "❌ Fail"}
                              </p>
                            );
                          })()}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
